/**
 * KPI Summary Card Component (Sprint 12, F001)
 *
 * Renders a compact financial health dashboard with:
 * - 7 KPI tiles: Revenue, Gross Profit, EBITDA, NPBT, GP%, EBITDA%, Net%
 * - Mini P&L summary table (6 rows)
 * - Loading skeleton during data fetch
 * - Empty state when no period selected
 * - Colour-coded margin percentages (green > 0, red < 0)
 *
 * Fetches P&L data via existing getProfitLoss() — shares React Query cache
 * with ProfitLossPanel (key: ['financial', 'profit-loss', periodId]).
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { getProfitLoss } from '@/lib/api/financial';
import type { ProfitLossLineItem } from '@/types/financial';
import { cascade } from '../../../../../shared/calc/cascade';
import { CalcInputError, fromCents, toCents } from '../../../../../shared/calc/money';
import { deductContraRevenueCents } from '../../../../../shared/calc/revenue';

// =============================================================================
// Types
// =============================================================================

export interface KpiSummaryCardProps {
  periodId: string | undefined;
}

interface KpiMetrics {
  revenue: number | null;
  grossProfit: number | null;
  ebitda: number | null;
  npbt: number | null;
  totalExpenses: number | null;
  cogs: number | null;
  gpMargin: number | null;
  ebitdaMargin: number | null;
  netMargin: number | null;
}

// =============================================================================
// Currency & Percentage Formatting
// =============================================================================

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number | null): string {
  if (value === null) return '-';
  if (value < 0) {
    return `(${currencyFormatter.format(Math.abs(value))})`;
  }
  return currencyFormatter.format(value === 0 ? 0 : value);
}

function formatPercentage(value: number | null): string {
  if (value === null) return '-';
  return `${value.toFixed(1)}%`;
}

// =============================================================================
// KPI Calculation
// =============================================================================

function calculateKpis(plItems: ProfitLossLineItem[]): KpiMetrics {
  try {
    // Show recorded amounts, as in the server's raw-period cascade; no YTD scaling.
    const result = cascade({
      periodLabel: '', periodType: 'historical', monthsInPeriod: 12, fyEndMonth: 6,
      profitLoss: plItems.map(item => ({ lineItem: item.lineItem, amountCents: toCents(('storedAmount' in item ? item.storedAmount : item.amount) as string | number) })),
      balanceSheet: [],
    });
    const revenueCents = deductContraRevenueCents(result.operatingIncomeCents, result.contraRevenueCents);
    const margin = (cents: number) => revenueCents === 0 ? null : (cents / revenueCents) * 100;
    return {
      revenue: fromCents(revenueCents),
      grossProfit: fromCents(result.grossProfitCents),
      ebitda: fromCents(result.ebitdaCents),
      npbt: fromCents(result.npbtCents),
      totalExpenses: fromCents(result.operatingExpensesCents),
      cogs: fromCents(result.cogsCents),
      gpMargin: margin(result.grossProfitCents),
      ebitdaMargin: margin(result.ebitdaCents),
      netMargin: margin(result.npbtCents),
    };
  } catch (error) {
    if (!(error instanceof CalcInputError)) throw error;
    return {
      revenue: null, grossProfit: null, ebitda: null, npbt: null,
      totalExpenses: null, cogs: null, gpMargin: null, ebitdaMargin: null, netMargin: null,
    };
  }
}

// =============================================================================
// Margin Colour Helper
// =============================================================================

function getMarginColourClass(value: number | null): string {
  if (value === null) return 'text-muted-foreground';
  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
  return 'text-muted-foreground';
}

// =============================================================================
// Sub-components
// =============================================================================

function KpiTile({
  testId,
  label,
  value,
  colourClass,
}: {
  testId: string;
  label: string;
  value: string;
  colourClass?: string;
}) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
        <p className={cn('text-2xl font-bold', colourClass)}>{value}</p>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div data-testid="kpi-loading-skeleton" className="space-y-4">
      {/* Tile skeletons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-4 pb-3">
              <Skeleton className="h-3 w-16 mb-2" />
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Table skeleton */}
      <Separator />
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex justify-between">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniPlTable({ metrics }: { metrics: KpiMetrics }) {
  const rows = [
    { label: 'Revenue / Sales', amount: metrics.revenue, bold: false },
    { label: 'Cost of Goods Sold (COGS)', amount: metrics.cogs === null ? null : -metrics.cogs, bold: false },
    { label: 'Gross Profit', amount: metrics.grossProfit, bold: true, separatorAbove: true },
    { label: 'Total Operating Expenses', amount: metrics.totalExpenses === null ? null : -metrics.totalExpenses, bold: false },
    { label: 'NPBT', amount: metrics.npbt, bold: true, separatorAbove: true },
    { label: 'EBITDA', amount: metrics.ebitda, bold: true },
  ];

  return (
    <table data-testid="kpi-mini-pl-table" className="w-full text-sm">
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.label}
            role="row"
            className={cn(
              'border-b border-border/50',
              row.separatorAbove && 'border-t-2 border-t-border',
            )}
          >
            <td className={cn('py-2', row.bold && 'font-semibold')}>{row.label}</td>
            <td className={cn('py-2 text-right tabular-nums', row.bold && 'font-semibold')}>
              {formatCurrency(row.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export const KpiSummaryCard: React.FC<KpiSummaryCardProps> = ({ periodId }) => {
  const { data: plItems, isLoading, isError } = useQuery({
    queryKey: ['financial', 'profit-loss', periodId],
    queryFn: () => getProfitLoss(periodId!),
    enabled: !!periodId,
  });

  // Empty state — no period selected
  if (!periodId) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base font-medium">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-testid="kpi-empty-state" className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground">Select a financial period to view summary</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base font-medium">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base font-medium">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-destructive">Failed to load financial data</p>
            <p className="text-xs text-muted-foreground mt-1">Try refreshing the page</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate KPIs from P&L data
  const metrics = calculateKpis(plItems || []);

  return (
    <Card data-testid="kpi-summary-card">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-base font-medium">Financial Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPI Tiles Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Currency KPIs - top row */}
          <KpiTile testId="kpi-tile-revenue" label="Revenue" value={formatCurrency(metrics.revenue)} />
          <KpiTile testId="kpi-tile-gross-profit" label="Gross Profit" value={formatCurrency(metrics.grossProfit)} />
          <KpiTile testId="kpi-tile-ebitda" label="EBITDA" value={formatCurrency(metrics.ebitda)} />
          <KpiTile testId="kpi-tile-npbt" label="NPBT" value={formatCurrency(metrics.npbt)} />

          {/* Percentage KPIs - bottom row */}
          <KpiTile
            testId="kpi-tile-gp-margin"
            label="GP Margin"
            value={formatPercentage(metrics.gpMargin)}
            colourClass={getMarginColourClass(metrics.gpMargin)}
          />
          <KpiTile
            testId="kpi-tile-ebitda-margin"
            label="EBITDA Margin"
            value={formatPercentage(metrics.ebitdaMargin)}
            colourClass={getMarginColourClass(metrics.ebitdaMargin)}
          />
          <KpiTile
            testId="kpi-tile-net-margin"
            label="Net Margin"
            value={formatPercentage(metrics.netMargin)}
            colourClass={getMarginColourClass(metrics.netMargin)}
          />
        </div>

        {/* Separator between tiles and table */}
        <Separator />

        {/* Mini P&L Summary Table */}
        <MiniPlTable metrics={metrics} />
      </CardContent>
    </Card>
  );
};

export default KpiSummaryCard;
